import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Undo2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * L2 "Return to Department" (rework / bounce-back) modal. Captures a mandatory
 * reason and posts to /tickets/{id}/return-to-department.
 */
export default function ReturnToDepartmentModal({ open, onOpenChange, ticket, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason for rework");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/return-to-department`, { reason: reason.trim() });
      toast.success(
        data.sent ? `Returned to department · email sent to ${data.to?.[0] || "POC"}` : "Returned to department",
      );
      setReason("");
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to return ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="return-to-department-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Undo2 className="w-4 h-4 text-orange-600" /> Return to Department
          </DialogTitle>
          <DialogDescription>
            Send <strong>{ticket?.code}</strong> back to the department for an updated resolution. The POC will be
            emailed your comments with a fresh resolution link.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 flex items-start gap-2 text-xs text-orange-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          The ticket status will change to <strong>Pending Department Action</strong> until the department resubmits.
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Reason for Rework <span className="text-rose-500">*</span>
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g. Resolution provided does not address the refund amount requested by the customer. Please review and provide updated action taken."
            data-testid="rework-reason-input"
            className="resize-none"
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="rework-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reason.trim()}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="rework-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />}
            Return to Department
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
